import classNames from 'classnames';
import qs from 'query-string';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { InputProps } from '../../../common/components/form-elements/Input';
import * as Actions from '../../../common/services/customer/Actions';
import * as CustomerService from '../../../common/services/customer/Service';
import { CustomerError } from '../../../common/services/customer/Types';
import { AddVehicleDTO } from '../../../common/types/dto/AddVehicleDTO';
import { CustomerDataDTO } from '../../../common/types/dto/CustomerDataDTO';
import { VehicleDataDTO } from '../../../common/types/dto/VehicleDataDTO';
import imgChassiNumber from '../../assets/images/chassi-number.jpg';
import cssCheckbox from '../../assets/scss/Checkbox.scss';
import cssForms from '../../assets/scss/Forms.scss';
import cssMessages from '../../assets/scss/Messages.scss';
import { RootState } from '../../base/RootReducer';
import { config } from '../../common/';
import ValidateVehicle from '../../common/Components/ValidateVehicle';
import VehicleSelectV3 from '../../common/Components/VehicleSelectV3';
import { useLocation, useRouter } from '../../common/RouterHooks';
import { AppThunkDispatch } from '../../common/types';

interface DateInputProps {
    selected: Date;
    onChange: (date: Date) => void;
    dateFormat: string;
    locale: string;
}

interface VehiclesInfoContainerProps {
    editState: EditState;
    editDocState: EditDocState;
    removeState: RemoveState;
    newLicensePlate: InputProps;
    newLicensePlateForeign: InputProps;
    isForeignPlate: boolean;
    onIsForeignPlateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    newVin: InputProps;

    validateEmissionDate: DateInputProps;
    validateVin: InputProps;

    vehicles: VehicleDataDTO[];

    submitErrorText: string;
    removeErrorText: string;

    btnEditVehiclesInfo: {
        onClick: (event: React.MouseEvent<{}> | null) => void;
        value: string;
        disabled: boolean;
    };
    btnRemoveVehiclesInfo: {
        onClick: (e: React.MouseEvent<{}> | null, vehcode: number) => void;
        disabled: boolean;
    };
    onValidateVehicleSuccess: () => void;
    onVehicleChange: (event: any) => void;

    IsValidationAccepted: boolean;
    IsValidationNotValid: boolean;
    IsValidationManualRequired: boolean;
    validationErrorReason: string;
    isValidationSubmited: boolean;
    vehcode: number;
}

interface VehiclesInfoProps {
}

export interface EditState {
    isEditing: boolean;
    changeEditMode: (event: React.MouseEvent<{}> | null) => void;
}

export interface EditDocState {
    isEditingDoc: boolean;
    changeEditDocMode: (event: React.MouseEvent<{}> | null) => void;
}

export interface RemoveState {
    isRemoving: boolean;
    changeRemoveMode: (event: React.MouseEvent<{}> | null) => void;
}

interface ReduxStateProps {
    isCommunicating: boolean;
    customerInfo: CustomerDataDTO | undefined;
}

interface ReduxDispatchProps {
    customerActions: typeof Actions;
    customerServiceActions: CustomerService.ServiceActions;
    dispatch: AppThunkDispatch;
}

interface VehiclesInfoState {
    isEditing: boolean;
    isEditingDoc: boolean;
    isRemoving: boolean;
    newLicensePlate: string;
    newLicensePlateValMsg: string;
    newLicensePlateForeign: string;
    newLicensePlateForeignValMsg: string;
    isForeignPlate: boolean;
    newVin: string;
    newVinValMsg: string;
    validateEmissionDate: Date;
    validateVin: string;
    vehicles: VehicleDataDTO[];
    submitErrorText: string;
    removeErrorText: string;
    vehcode: number;
    isValidationAccepted: boolean;
    isValidationNotValid: boolean;
    isValidationManualRequired: boolean;
    validationErrorReason: string;
    isValidationSubmited: boolean;
    isRefreshed: boolean;
    isCommunicating: boolean;
}

type VehiclesInfoProperties =
    VehiclesInfoProps
    & ReduxStateProps
    & ReduxDispatchProps;

const VehiclesInfo: React.FC<VehiclesInfoProperties> = (Props) => {
    const { t } = useTranslation();
    const location = useLocation();
    const router = useRouter();

    const initialState: VehiclesInfoState = {
        isEditing: false,
        isEditingDoc: false,
        isRemoving: false,
        newLicensePlate: '',
        newLicensePlateValMsg: '',
        newLicensePlateForeign: '',
        newLicensePlateForeignValMsg: '',
        isForeignPlate: false,
        newVin: '',
        newVinValMsg: '',
        validateEmissionDate: new Date(0),
        validateVin: '',
        vehicles: [],
        submitErrorText: '',
        removeErrorText: '',
        vehcode: -1,
        isValidationAccepted: false,
        isValidationNotValid: false,
        isValidationManualRequired: false,
        validationErrorReason: '',
        isValidationSubmited: false,
        isRefreshed: false,
        isCommunicating: false,
    };
    const [state, setState]: [VehiclesInfoState, React.Dispatch<any>] = useReducer(
        (oldState, newState) => ({ ...oldState, ...newState }), initialState,
    );

    const delay = ((ms: number) => (new Promise((res) => setTimeout(res, ms))));

    async function handleRefreshVehicleData() {
        if (state.isValidationSubmited && !state.isValidationAccepted && !state.isValidationNotValid && !state.isValidationManualRequired) {
            const vehicleRefreshData = await Props.customerServiceActions.vehicleData(state.vehcode);
            if (vehicleRefreshData && vehicleRefreshData.vehcode === state.vehcode) {
                setState({
                    isRefreshed: true,
                    isValidationAccepted: vehicleRefreshData.isValidationAccepted,
                    isValidationNotValid: vehicleRefreshData.isValidationNotValid,
                    isValidationManualRequired: vehicleRefreshData.isValidationManualRequired,
                    validationErrorReason: vehicleRefreshData.validationErrorReason,
                    isValidationSubmited: vehicleRefreshData.isValidationSubmited,
                });
            }
            if (vehicleRefreshData.validationState === -1) {
                refreshVehicleTimmer();
            }
        } else {
            refreshVehicleTimmer();
        }
    }

    async function refreshVehicleTimmer() {
        await delay(5000);
        handleRefreshVehicleData();
    }

    useEffect(() => {
        if (state.isValidationSubmited && !state.isValidationAccepted && !state.isValidationNotValid && !state.isValidationManualRequired) {
            handleRefreshVehicleData();
        }
    }, [state.isValidationSubmited, state.isValidationAccepted, state.isValidationNotValid, state.isValidationManualRequired, state.validationErrorReason]);

    useEffect(() => {
        if (Props.customerInfo && Props.customerInfo.vehicles) {
            const vehTempue = state.vehicles.find((x: VehicleDataDTO) => x.vehcode === state.vehcode);
            setState({
                vehicles: Props.customerInfo.vehicles,
                vehicle: vehTempue,
                isValidationAccepted: vehTempue ? vehTempue.isValidationAccepted : false,
                isValidationNotValid: vehTempue ? vehTempue.isValidationNotValid : false,
                isValidationManualRequired: vehTempue ? vehTempue.isValidationManualRequired : false,
                validationErrorReason: vehTempue ? vehTempue.validationErrorReason : '',
                isValidationSubmited: vehTempue ? vehTempue.isValidationSubmited : false,
                isRefreshed: false,
            });
        }
    }, [Props.customerInfo, state.vehcode]);

    useEffect(() => {
        const op = qs.parse(location.search).op;
        if (op && op === 'addVehicle') {
            router.history.replace({ search: '' });
            // setState({ isEditing: true });
        }
    }, [location.search]);

    const onValidateVehicleSuccess = async () => {
        window.location.reload();
    };

    const validateForm = () => {
        if (Props.isCommunicating) {
            return false;
        }
        let isValid: boolean = true;
        if (!state.isForeignPlate) {
            if (state.newLicensePlate === '') {
                setState({ newLicensePlateValMsg: t('mandatory') });
                isValid = false;
            }
        } else { // Foreign plate
            if (state.newLicensePlateForeign === '') {
                setState({ newLicensePlateForeignValMsg: t('mandatory') });
                isValid = false;
            }
        }
        if (state.newVin) {
            const vinUppercase = state.newVin.toUpperCase();
            const isVinValid = config.Constants.Regex.VinValidation.test(vinUppercase);
            if (!isVinValid) {
                setState({ newVinValMsg: t('signup:errors:vinValidationRegex') });
                isValid = false;
            }
        }
        return isValid;
    };

    const changeEditMode = (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }

        setState({
            isEditing: !state.isEditing,
        });
        // handleInit(Props);
    };

    const changeEditDocMode = (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }

        setState({
            isEditingDoc: !state.isEditingDoc,
        });
        // handleInit(Props);
    };

    const changeRemoveMode = (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }

        setState({
            isRemoving: !state.isRemoving,
        });
        // handleInit(Props);
    };

    const onVehicleChange = (event: any) => {
        if (event && event !== state.vehcode) {
            const vehTemplp = state.vehicles.find((x: VehicleDataDTO) => x.vehcode === event);
            setState({
                vehcode: event,
                isValidationAccepted: vehTemplp ? vehTemplp.isValidationAccepted : false,
                isValidationNotValid: vehTemplp ? vehTemplp.isValidationNotValid : false,
                isValidationManualRequired: vehTemplp ? vehTemplp.isValidationManualRequired : false,
                validationErrorReason: vehTemplp ? vehTemplp.validationErrorReason : '',
                isValidationSubmited: vehTemplp ? vehTemplp.isValidationSubmited : false,
                isRefreshed: false,
            });
        }
    };

    const onEditSubmit = async (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }
        if (!validateForm()) {
            return;
        }
        try {
            const addVehicleDTO: AddVehicleDTO = {
                licensePlate: state.newLicensePlate,
                licensePlateForeign: state.newLicensePlateForeign,
                isForeignPlate: state.isForeignPlate,
                vin: state.newVin,
            };
            // TODO : We could just validate using the info we have about user's vehicles in browser
            const isValid = await Props.customerServiceActions.addVehicle(addVehicleDTO);
            if (isValid) {
                Props.dispatch(Notifications.success({
                    title: t('success'),
                    message: t('profile:addVehicle:success'),
                    position: 'tc',
                    autoDismiss: 5,
                }));
                setState({
                    submitErrorText: '',
                    isEditing: false,
                });
                // handleInit(Props);
                await Props.customerServiceActions.customerData();
            } else {
                Props.dispatch(Notifications.error({
                    title: t('success'),
                    message: t('profile:addVehicle:error'),
                    position: 'tc',
                    autoDismiss: 5,
                }));
            }
        } catch (error) {
            if (error instanceof CustomerError) {
                const exceptionType: string = error.customerErrorInfo.exceptionType || 'UnknownException';
                const exceptionErrorMsg: string = t(`exception:${exceptionType.split('.').pop()}`);
                setState({ submitErrorText: exceptionErrorMsg });
                Props.dispatch(Notifications.error({
                    title: t('error'),
                    message: exceptionErrorMsg,
                    position: 'tc',
                    autoDismiss: 5,
                }));
            } else {
                throw error;
            }
        }
    };

    const onRemoveSubmit = async (e: React.MouseEvent<{}> | null, vehcode: number) => {
        if (e) {
            e.preventDefault();
        }
        setState({ isCommunicating: true });
        try {
            await Props.customerServiceActions.deleteVehicle(vehcode);
            Props.dispatch(Notifications.success({
                title: t('success'),
                message: t('profile:deleteVehicle:success'),
                position: 'tc',
                autoDismiss: 5,
            }));
            setState({
                removeErrorText: '',
                isRemoving: false,
                isCommunicating: false,
            });
            // handleInit(Props);
            await Props.customerServiceActions.customerData();
        } catch (error) {
            setState({ isCommunicating: false });
            if (error instanceof CustomerError) {
                const exceptionType: string = error.customerErrorInfo.exceptionType || 'UnknownException';
                const exceptionErrorMsg: string = t(`exception:${exceptionType.split('.').pop()}`);
                setState({ removeErrorText: exceptionErrorMsg });
                Props.dispatch(Notifications.error({
                    title: t('error'),
                    message: exceptionErrorMsg,
                    position: 'tc',
                    autoDismiss: 5,
                }));
            } else {
                throw error;
            }
        }
    };

    const onNewLicensePlateChange = (event: React.FormEvent<HTMLInputElement>) => {
        setState({ newLicensePlate: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            setState({ newLicensePlateValMsg: '' });
        } else {
            setState({ newLicensePlateValMsg: t('mandatory') });
        }
    };

    const onNewLicensePlateForeignChange = (event: React.FormEvent<HTMLInputElement>) => {
        setState({ newLicensePlateForeign: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            setState({ newLicensePlateForeignValMsg: '' });
        } else {
            setState({ newLicensePlateForeignValMsg: t('mandatory') });
        }
    };

    const onIsForeignPlateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState({
            newLicensePlateForeign: '',
            newLicensePlate: '',
            newLicensePlateForeignValMsg: '',
            newLicensePlateValMsg: '',
            isForeignPlate: event.target.checked,
        });
    };

    const onNewVinChange = (event: React.FormEvent<HTMLInputElement>) => {
        setState({
            newVin: event.currentTarget.value,
            newVinValMsg: '',
        });
    };

    const onValidateEmissionDateChange = (date: Date) => {
        setState({ validateEmissionDate: date });
    };

    const onValidateVinChange = (event: React.FormEvent<HTMLInputElement>) => {
        setState({ validateVin: event.currentTarget.value });
    };

    const vehTemp = state.vehcode > -1 ? state.vehicles.find((x: VehicleDataDTO) => x.vehcode === state.vehcode) : null;
    const newProps: VehiclesInfoContainerProps = {
        editState: {
            isEditing: state.isEditing,
            changeEditMode,
        },
        editDocState: {
            isEditingDoc: state.isEditingDoc,
            changeEditDocMode,
        },
        removeState: {
            isRemoving: state.isRemoving,
            changeRemoveMode,
        },
        newLicensePlate: {
            inputProps: {
                value: state.newLicensePlate,
                onChange: onNewLicensePlateChange,
                // maxLength: config.Validations.LicensePlate.MaxLength, // No need since is being used <InputMask> now TODO : Remove it after test
            },
            errorText: state.newLicensePlateValMsg,
            hintText: '',
        },
        newLicensePlateForeign: {
            inputProps: {
                value: state.newLicensePlateForeign,
                onChange: onNewLicensePlateForeignChange,
                maxLength: config.Validations.LicensePlateForeign.MaxLength,
            },
            errorText: state.newLicensePlateForeignValMsg,
            hintText: '',
        },
        isForeignPlate: state.isForeignPlate,
        onIsForeignPlateChange,
        newVin: {
            inputProps: {
                value: state.newVin,
                onChange: onNewVinChange,
                // maxLength: config.Validations.VIN.MaxLength,
            },
            errorText: state.newVinValMsg,
            hintText: '',
        },
        validateVin: {
            inputProps: {
                value: state.validateVin,
                onChange: onValidateVinChange,
                maxLength: config.Validations.VIN.MaxLength,
            },
            hintText: '',
        },
        validateEmissionDate: {
            onChange: onValidateEmissionDateChange,
            selected: state.validateEmissionDate,
            dateFormat: 'yyyy-MM-dd',
            locale: 'pt',
        },
        vehicles: state.vehicles,
        submitErrorText: state.submitErrorText,
        removeErrorText: state.removeErrorText,
        btnEditVehiclesInfo: {
            onClick: onEditSubmit,
            value: t('signup:signup'),
            disabled: state.isCommunicating,
        },
        btnRemoveVehiclesInfo: {
            onClick: onRemoveSubmit,
            disabled: state.isCommunicating,
        },
        onValidateVehicleSuccess,
        onVehicleChange,
        vehcode: state.vehcode,
        IsValidationAccepted: state.isRefreshed ? state.isValidationAccepted : vehTemp ? vehTemp.isValidationAccepted : false,
        IsValidationNotValid: state.isRefreshed ? state.isValidationNotValid : vehTemp ? vehTemp.isValidationNotValid : false,
        IsValidationManualRequired: state.isRefreshed ? state.isValidationManualRequired : vehTemp ? vehTemp.isValidationManualRequired : false,
        validationErrorReason: state.isRefreshed ? state.validationErrorReason : vehTemp && vehTemp.validationErrorReason ? vehTemp.validationErrorReason : '',
        isValidationSubmited: state.isRefreshed ? state.isValidationSubmited : vehTemp ? vehTemp.isValidationSubmited : false,
    };

    // ----------------------------

    return (
        <React.Fragment>
            <div className={classNames(['cell', 'large-10', 'large-offset-1', 'columns'])}>
                <div className={'tabs-content'} data-tabs-content="collapsing-tabs">
                    <div className={classNames(['tabs-panel', 'is-active'])} id="dados-pessoais">
                        <div className={classNames(['row', 'medium-unstack', 'dados-pacotes', 'margin-top-1'])}>
                            {!newProps.editState.isEditing && !newProps.editDocState.isEditingDoc && !newProps.removeState.isRemoving && (
                                <React.Fragment>
                                    <VehicleSelectV3 onChange={newProps.onVehicleChange} isMini={false} />
                                    {
                                        (!config.EnableVehicleValidation || newProps.vehicles == null || newProps.vehicles.length === 0) ? null :
                                            ((!newProps.IsValidationAccepted && !newProps.isValidationSubmited) || newProps.IsValidationNotValid || newProps.IsValidationManualRequired) ?
                                                <React.Fragment>
                                                    <div className={classNames(['columns', 'medium-12', 'padding-left-0', 'padding-right-0'])}>
                                                        <hr />
                                                    </div>
                                                    <div className={classNames(['columns', 'medium-12', 'padding-left-0', 'padding-right-0'])}>
                                                        <p className={classNames(['margin-left-0', 'margin-bottom-0'])}>{t('profile:duaRequiredOffer')}</p>
                                                        <p className={classNames(['margin-left-0'])}>{t('profile:duaRequired')}</p>
                                                        <hr />
                                                    </div>
                                                </React.Fragment>
                                                : null
                                    }
                                    <div className={classNames(['columns', 'medium-12', 'padding-left-0', 'padding-right-0'])}>
                                        <div className={classNames(['small', 'button-group', 'dados-pacotes'])}>
                                            <button id="btAdicionarViatura" className={classNames(['button', 'adicionar'])} onClick={newProps.editState.changeEditMode}>{t('profile:vehicles.add')}</button>
                                            {newProps.vehicles && newProps.vehicles.length !== 0 && (
                                                <button id="btRemoverViatura" className={classNames(['button', 'cancelar', 'margin-left-1'])} onClick={newProps.removeState.changeRemoveMode}>{t('profile:vehicles.remove')}</button>
                                            )}
                                            {
                                                (!config.EnableVehicleValidation || newProps.vehicles == null || newProps.vehicles.length === 0) ? null :
                                                    ((!newProps.IsValidationAccepted && !newProps.isValidationSubmited) || newProps.IsValidationNotValid || newProps.IsValidationManualRequired)
                                                        ? <button id="btValidarViatura" className={classNames(['button', 'alterar', 'margin-left-1'])} onClick={newProps.editDocState.changeEditDocMode}>{newProps.IsValidationManualRequired ? t('profile:vehicles.validateRe') : t('profile:vehicles.validate')}</button>
                                                        : null
                                            }
                                        </div>
                                    </div>
                                </React.Fragment>
                            )}
                            {!newProps.editState.isEditing && !newProps.editDocState.isEditingDoc && !newProps.removeState.isRemoving && (
                                <div className={classNames(['columns', 'medium-12', 'padding-left-0', 'padding-right-0'])}>
                                    {
                                        (!config.EnableVehicleValidation || !newProps.isValidationSubmited) ? null :
                                            newProps.IsValidationManualRequired ?
                                                <React.Fragment>
                                                    <div className={cssMessages.infoMsg}>
                                                        <i className="fa fa-check" style={{ marginRight: '5px' }} />
                                                        {t('profile:validateVehManualRequired')}
                                                    </div>
                                                    {state.validationErrorReason && <div className={cssMessages.infoMsg}>
                                                        <i className="fa fa-info-circle" style={{ marginRight: '5px' }} />
                                                        <b>Notificão</b><br />
                                                        {state.validationErrorReason}
                                                    </div>}
                                                </React.Fragment> :
                                                newProps.IsValidationAccepted ?
                                                    <div className={cssMessages.successMsg}>
                                                        <i className="fa fa-check" style={{ marginRight: '5px' }} />
                                                        {t('profile:validateVehDone')}
                                                    </div> :
                                                    newProps.IsValidationNotValid ?
                                                        <div className={cssMessages.warningMsg}>
                                                            <i className="fa fa-info-circle" style={{ marginRight: '5px' }} />
                                                            {t('profile:validateVehNotValid')}
                                                        </div> :
                                                        <div className={cssMessages.infoMsg}>
                                                            <i className="fa fa-info-circle" style={{ marginRight: '5px' }} />
                                                            {t('profile:validateVehSubmited')}
                                                        </div>
                                    }
                                </div>)
                            }
                            {newProps.removeState.isRemoving && (
                                <div className={classNames(['columns', 'medium-12', 'padding-left-0', 'padding-right-0'])}>
                                    <div className={'tabs-content'} data-tabs-content="collapsing-tabs">
                                        <div className={classNames(['tabs-panel', 'is-active'])} id="panelAdicionarViatura">
                                            <p className={classNames(['margin-top-2', 'text-bold'])}>
                                                {t('common:confirmTitle')}
                                            </p>
                                            <p className={'margin-top-0'}>
                                                {t('profile:deleteVehicle.confirmationMessage')}
                                            </p>

                                            <div className={classNames(['small', 'button-group', 'dados-pacotes', 'margin-top-2'])}>
                                                <button className={classNames(['button', 'alterar'])} onClick={(e) => newProps.btnRemoveVehiclesInfo.onClick(e, newProps.vehcode)} disabled={newProps.btnRemoveVehiclesInfo.disabled}>{newProps.btnRemoveVehiclesInfo.disabled ? t('loadingText') : t('delete')}</button>
                                                <button className={classNames(['button', 'cancelar', 'margin-left-1'])} onClick={newProps.removeState.changeRemoveMode}>{t('cancel')}</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {newProps.editState.isEditing && (
                                <div className={classNames(['columns', 'medium-12', 'padding-left-0', 'padding-right-0'])}>
                                    <h3>{t('profile:subTitleEdit3')}</h3>
                                    <form>
                                        <div className={classNames(['grid-container', 'fluid', 'padding-left-0', 'blocoAdicionarViatura'])}>
                                            <div className={classNames(['grid-x', 'grid-margin-x'])}>
                                                <div className={classNames(['cell', 'medium-6', 'small-12'])}>
                                                    <label>
                                                        {t('signup:formFields.licensePlate')}*
                                                        {!newProps.isForeignPlate && <React.Fragment>
                                                            <InputMask type="text" {...newProps.newLicensePlate.inputProps} maskChar="" mask="**-**-**" className={cssForms.inputUppercase} />
                                                            <div className="error-message">{newProps.newLicensePlate.errorText}</div>
                                                        </React.Fragment>}
                                                        {newProps.isForeignPlate && <React.Fragment>
                                                            <input type="text" {...newProps.newLicensePlateForeign.inputProps} className={cssForms.inputUppercase} />
                                                            <div className="error-message">{newProps.newLicensePlateForeign.errorText}</div>
                                                        </React.Fragment>}
                                                    </label>
                                                </div>
                                                <div className={classNames(['cell', 'medium-6', 'small-12'])}>
                                                    <label>
                                                        {t('signup:formFields.vin')}
                                                        <InputMask {...newProps.newVin.inputProps} maskChar="" mask="*****************" type="text" className={cssForms.inputUppercase} />
                                                        <div className="error-message">{newProps.newVin.errorText}</div>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className={classNames(['grid-x', 'margin-top-1'])}>
                                                <div className={classNames(['columns', 'medium-6', 'small-12', 'padding-left-0', (config.BrandEnv === 'BMW' ? cssCheckbox.bmw : cssCheckbox.mini)])}>
                                                    <p className={classNames(['p-top-1', 'my-bmw-conta-de-utilizador-viatura-info'])}>{t('signup:extraInfo.licensePlate')}</p>
                                                    <div className={cssCheckbox.inputCheckboxSmall}>
                                                        <label className={cssCheckbox.center}>
                                                            <input id="chkmatricula" type="checkbox" checked={newProps.isForeignPlate} onChange={newProps.onIsForeignPlateChange} />
                                                            <i />
                                                            <span>{t('isForeignPlate')}</span>
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className={classNames(['columns', 'medium-6', 'small-12', 'padding-left-0'])}>
                                                    <img className={'hide-for-small'} src={imgChassiNumber} />
                                                    <img className={'hide-for-large'} src={imgChassiNumber} />
                                                </div>
                                            </div>
                                        </div>
                                        <p className={'margin-top-2'}>
                                            {t(`profile:addVehicleNote`)}<a href={`mailto:${t('profile:noteEmail')}`}>{t(`profile:noteEmail`)}</a>.
                                        </p>
                                        <div className={classNames(['small', 'button-group', 'dados-pacotes', 'margin-top-2'])}>
                                            <button className={classNames(['button', 'alterar'])} {...newProps.btnEditVehiclesInfo}>{newProps.btnEditVehiclesInfo.disabled ? t('loadingText') : t('add')}</button>
                                            <button className={classNames(['button', 'cancelar', 'margin-left-1'])} onClick={newProps.editState.changeEditMode}>{t('cancel')}</button>
                                        </div>
                                    </form>
                                </div>
                            )}
                            {newProps.editDocState.isEditingDoc && (
                                <div className={classNames(['columns', 'medium-12', 'padding-left-0', 'padding-right-0'])}>
                                    <ValidateVehicle
                                        validation={newProps.editDocState.isEditingDoc}
                                        vehcode={newProps.vehcode}
                                        onSucess={newProps.onValidateVehicleSuccess}
                                        onCancel={newProps.editDocState.changeEditDocMode}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = (store: RootState) => ({
    isCommunicating: store.customerState.addVehicle.isCommunicating,
    customerInfo: store.customerState.customerData.data.customerData,

    isDocCommunicating: store.customerState.uploadVehicleDocs.isCommunicating,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    customerActions: bindActionCreators(Actions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(VehiclesInfo);
